import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import EmailInput from '@/components/form/EmailInput.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import useRegisterTpay from '@/features/dashboard/paymentProviders/hooks/useRegisterTpay.ts';
import {
  tpayRegisterSchema,
  TpayRegisterFormData,
  tpayRegisterDefaultValues,
} from '@/features/dashboard/paymentProviders/schemas/tpayRegisterSchema.ts';
import { auth } from '@/lib/firebase.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';

const RegisterTpayModal = ({ onClose }: { onClose: () => void }) => {
  const registerMutation = useRegisterTpay();
  const { confirmWithCallback } = confirmDialogUseHook();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TpayRegisterFormData>({
    resolver: zodResolver(tpayRegisterSchema),
    defaultValues: {
      ...tpayRegisterDefaultValues,
      email: auth.currentUser?.email ?? '',
    },
  });

  const onSubmit = (data: TpayRegisterFormData) => {
    registerMutation.mutate(data.email, {
      onSuccess: (responseData) => {
        onClose();
        confirmWithCallback({
          title: 'Rejestracja TPay',
          description: (
            <p>
              Link aktywacyjny został wysłany na podany adres e-mail. Możesz
              teraz otworzyć formularz konfiguracyjny TPay lub zrobić to później
              korzystając z linku w wiadomości e-mail.
            </p>
          ),
          confirmText: 'Otwórz formularz TPay',
          onConfirm: () => {
            window.open(responseData.activationLink, '_blank');
          },
        });
      },
    });
  };

  return (
    <Modal onClose={onClose} className="max-w-[560px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader title="Rejestracja konta TPay" onClose={onClose} />
        <ModalContent>
          <div className="space-y-4">
            <p className="text-sm text-neutral-500">
              Jeśli masz już konto TPay, podaj adres e-mail powiązany z tym
              kontem. Jeśli nie, możesz użyć dowolnego adresu e-mail.
            </p>
            <EmailInput
              label="Adres e-mail"
              required
              register={register('email')}
              error={errors.email?.message}
            />
            <CheckboxInput
              label="Wyrażam zgodę na przekazanie podanego adresu e-mail do TPay SA w celu rejestracji konta."
              register={register('consent')}
              error={errors.consent?.message}
            />
          </div>
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={registerMutation.isPending}
          >
            Zarejestruj
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default RegisterTpayModal;
